import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EventIcon from '@mui/icons-material/Event'
import TodayIcon from '@mui/icons-material/Today'
import { IconButton } from '@mui/material'
import moment from 'moment'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { TechnicianStatus } from '../hooks/useSchedulingData'
import styles from '../styles.module.css'
import { simulatePatternStatus } from '../utils/scheduling-utils'

// Configure moment to start weeks on Sunday (standard US format)
moment.updateLocale('en', {
  week: {
    dow: 0, // Sunday is the first day of the week
  },
})

const localizer = momentLocalizer(moment)

interface CalendarViewProps {
  calendarDate: Date
  setCalendarDate: (date: Date) => void
  technicianStatuses: Map<string, TechnicianStatus[]>
  visibleTechnicianIds: Set<string>
  draftSchedulePatterns: Map<string, any>
  editingSchedules: Map<string, any>
  onDayClick: (date: Date) => void
  onTechnicianDayClick: (date: Date, technicianId: string) => void
}

export default function CalendarView({
  calendarDate,
  setCalendarDate,
  technicianStatuses,
  visibleTechnicianIds,
  draftSchedulePatterns,
  editingSchedules,
  onDayClick,
  onTechnicianDayClick,
}: CalendarViewProps) {
  // Custom date header for calendar cells
  const CustomDateHeader = ({ date, label }: { date: Date; label: string }) => {
    const dateKey = moment(date).format('YYYY-MM-DD')
    const dayStatuses = technicianStatuses.get(dateKey) || []

    // Only show statuses for visible technicians
    const visibleStatuses = dayStatuses.filter((status) =>
      visibleTechnicianIds.has(status.technicianObjectId),
    )

    // Check if this date is a holiday (any technician has holiday status)
    const isHoliday = dayStatuses.some((status) => status.status === 'holiday')
    // Get the holiday name if available (all techs should have same holiday name for a given date)
    const holidayName = dayStatuses.find((status) => status.holidayName)?.holidayName

    // Check if this date is an effective date for any schedule pattern changes
    const isEffectiveDate = Array.from(editingSchedules.entries()).some(
      ([techId, editingData]) => {
        if (!visibleTechnicianIds.has(techId)) return false
        const effectiveDateStr = moment(date).format('YYYY-MM-DD')
        return editingData.effectiveFrom === effectiveDateStr
      },
    )

    // Show preview changes for technicians being edited
    const shouldShowPreview = Array.from(draftSchedulePatterns.keys()).some(
      (techId) => visibleTechnicianIds.has(techId),
    )

    let showPreview = false
    if (shouldShowPreview) {
      // Check if any visible technician has pattern changes for this date
      for (const [techId, draftPattern] of draftSchedulePatterns.entries()) {
        if (!visibleTechnicianIds.has(techId)) continue

        const editingData = editingSchedules.get(techId)
        if (!editingData) continue

        const effectiveDate = new Date(editingData.effectiveFrom + 'T00:00:00')

        // Only show preview for dates on/after the effective date
        if (date >= effectiveDate) {
          const currentStatus = visibleStatuses.find(
            (s) => s.technicianObjectId === techId,
          )?.status
          const draftStatus = simulatePatternStatus(
            date,
            draftPattern?.weekPatterns || [],
            effectiveDate,
          )

          // Show preview ring if there's a change
          const currentWorking = currentStatus === 'working'
          const draftWorking = draftStatus === 'working'
          if (currentWorking !== draftWorking) {
            showPreview = true
            break
          }
        }
      }
    }

    return (
      <div
        className={`${styles.customDateHeader} ${
          moment().isSame(date, 'day') ? styles.today : ''
        } ${isHoliday ? styles.holiday : ''}`}
        onClick={() => onDayClick(date)}
      >
        <div className={styles.dayNumber}>
          {label}
          {isEffectiveDate && (
            <EventIcon className={styles.effectiveDateIcon} />
          )}
          {isHoliday && holidayName && (
            <div className={styles.holidayName}>{holidayName}</div>
          )}
        </div>

        {/* Technician status indicators */}
        <div className={styles.technicianIndicators}>
          {visibleStatuses.map((status) => {
            // Check if this specific technician has preview changes
            const draftPattern = draftSchedulePatterns.get(
              status.technicianObjectId,
            )
            const editingData = editingSchedules.get(status.technicianObjectId)

            let hasPreview = false
            let draftStatus: 'off' | 'working' | null = null
            if (draftPattern && editingData) {
              const effectiveDate = new Date(
                editingData.effectiveFrom + 'T00:00:00',
              )

              if (date >= effectiveDate) {
                draftStatus = simulatePatternStatus(
                  date,
                  draftPattern?.weekPatterns || [],
                  effectiveDate,
                )
                const currentWorking = status.status === 'working'
                const draftWorking = draftStatus === 'working'
                hasPreview = currentWorking !== draftWorking
              }
            }

            // Use draft status for coloring if there's a preview, otherwise use current status
            const displayStatus =
              hasPreview && draftStatus ? draftStatus : status.status

            return (
              <div
                key={status.technicianObjectId}
                className={`${styles.techIndicator} ${styles[displayStatus]} ${
                  status.isHumanTouched ? styles.humanTouched : ''
                } ${hasPreview ? styles.hasPreview : ''}`}
                title={`${status.name}: ${status.status}${
                  status.isHumanTouched ? ' (manually set)' : ''
                }${hasPreview ? ` (will change to ${draftStatus})` : ''}${
                  isHoliday ? ` - ${holidayName || 'Holiday'}` : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation() // Prevent day click from firing
                  onTechnicianDayClick(date, status.technicianObjectId)
                }}
                style={{ cursor: 'pointer' }}
              >
                {status.initials}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Custom toolbar for calendar navigation
  const CustomToolbar = ({
    date,
    label,
    onNavigate,
  }: {
    date: Date
    label: string
    onNavigate: (action: string) => void
  }) => {
    return (
      <div className={styles.customToolbar}>
        <span className={styles.toolbarLabel}>{label}</span>
        <div className={styles.toolbarNavigation}>
          <IconButton
            size='small'
            className={styles.navIconButton}
            onClick={() => onNavigate('PREV')}
            title='Previous Month'
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size='small'
            className={styles.navIconButton}
            onClick={() => onNavigate('TODAY')}
            title='Current Month'
          >
            <TodayIcon />
          </IconButton>
          <IconButton
            size='small'
            className={styles.navIconButton}
            onClick={() => onNavigate('NEXT')}
            title='Next Month'
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
        <div></div>
      </div>
    )
  }

  // Calendar components customization
  const calendarComponents = {
    month: {
      dateHeader: CustomDateHeader,
    },
    toolbar: CustomToolbar,
  }

  return (
    <div style={{ height: '600px', width: '100%' }}>
      <Calendar
        localizer={localizer}
        events={[]}
        date={calendarDate}
        onNavigate={(date) => setCalendarDate(date)}
        views={['month']}
        defaultView='month'
        components={calendarComponents}
        className={styles.customCalendar}
      />
    </div>
  )
}
